/**
 * Portal in-app navigation stack for back button, edge swipe, and hardware back, plus a
 * forward stack for the iOS right-edge swipe.
 * Skips auth-only routes so authenticated users do not return to login screens.
 */

import type { NavigateFunction } from 'react-router-dom';

const AUTH_ONLY_EXACT = new Set([
    '/login',
    '/aria/login',
    '/login-by-token',
    '/auto-login',
    '/password-forget',
]);

const AUTH_ONLY_PREFIXES = [
    '/reset-forget-password',
    '/create-account',
    '/aria/create-account',
    '/verify-email',
    '/aria/verify-email',
];

const BACK_URL_STORAGE_KEY = 'back_url';

let navigationStack: string[] = [];
/** Entries ahead of the current route (nearest first) after going back. Cleared on any new PUSH. */
let forwardStack: string[] = [];
/** Direction of the POP we started ourselves, so a path that is both behind and ahead resolves correctly. */
let pendingPopDirection: 'back' | 'forward' | null = null;

export interface PortalBackOptions {
    explicitTarget?: string;
    /** When false, do not navigate to fallback (native hardware back at root). */
    useFallback?: boolean;
}

export function isAuthOnlyPath(pathname: string): boolean {
    const path = pathname.split('?')[0];
    if (AUTH_ONLY_EXACT.has(path)) {
        return true;
    }
    return AUTH_ONLY_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
}

export function getAuthenticatedHomePath(): string {
    return '/';
}

export function resetNavigationHistory(path = getAuthenticatedHomePath()): void {
    navigationStack = [path];
    forwardStack = [];
    pendingPopDirection = null;
}

function trimNavigationStack(): void {
    if (navigationStack.length > 100) {
        navigationStack = navigationStack.slice(-50);
    }
}

function pathnameOf(fullPath: string): string {
    return fullPath.split('?')[0];
}

export function recordNavigation(fullPath: string): void {
    const last = navigationStack[navigationStack.length - 1];
    if (last === fullPath) {
        return;
    }

    // A new PUSH discards the browser's forward entries, so ours go too.
    forwardStack = [];

    // Tab / query-only changes on the same page replace the current stack entry.
    if (last && pathnameOf(last) === pathnameOf(fullPath)) {
        navigationStack[navigationStack.length - 1] = fullPath;
        return;
    }

    navigationStack.push(fullPath);
    trimNavigationStack();
}

export function replaceLastNavigation(fullPath: string): void {
    if (navigationStack.length === 0) {
        navigationStack.push(fullPath);
        return;
    }
    navigationStack[navigationStack.length - 1] = fullPath;
}

/** Align tracked stack after browser POP (edge swipe, hardware back, navigate(delta)). */
export function syncNavigationStackToPath(fullPath: string): void {
    const direction = pendingPopDirection;
    pendingPopDirection = null;

    const forwardIdx = forwardStack.indexOf(fullPath);
    const backIdx = navigationStack.lastIndexOf(fullPath);

    // Forward: move everything up to and including the target from the forward stack.
    if (forwardIdx >= 0 && (direction === 'forward' || backIdx < 0)) {
        navigationStack.push(...forwardStack.slice(0, forwardIdx + 1));
        forwardStack = forwardStack.slice(forwardIdx + 1);
        trimNavigationStack();
        return;
    }

    // Back: keep what we passed over so the user can go forward to it again.
    if (backIdx >= 0) {
        forwardStack = [...navigationStack.slice(backIdx + 1), ...forwardStack];
        navigationStack = navigationStack.slice(0, backIdx + 1);
        return;
    }

    navigationStack.push(fullPath);
    forwardStack = [];
    trimNavigationStack();
}

/**
 * Negative delta for navigate(), skipping auth-only entries when going back.
 * Returns 0 when there is no safe prior route.
 */
export function getSafeBackDelta(): number {
    if (navigationStack.length < 2) {
        return 0;
    }
    const currentIndex = navigationStack.length - 1;
    for (let i = currentIndex - 1; i >= 0; i--) {
        const pathOnly = pathnameOf(navigationStack[i]);
        if (!isAuthOnlyPath(pathOnly)) {
            return i - currentIndex;
        }
    }
    return 0;
}

export function getPreviousNavigationPath(): string | null {
    const delta = getSafeBackDelta();
    if (delta === 0) {
        return null;
    }
    const currentIndex = navigationStack.length - 1;
    return navigationStack[currentIndex + delta] ?? null;
}

export function canNavigateBackSafely(): boolean {
    return getSafeBackDelta() !== 0;
}

/**
 * Positive delta for navigate() to the nearest forward entry that isn't auth-only.
 * Returns 0 when there is nothing safe ahead.
 */
export function getSafeForwardDelta(): number {
    for (let i = 0; i < forwardStack.length; i++) {
        if (!isAuthOnlyPath(pathnameOf(forwardStack[i]))) {
            return i + 1;
        }
    }
    return 0;
}

export function canNavigateForwardSafely(): boolean {
    return getSafeForwardDelta() !== 0;
}

/** Forward counterpart of navigatePortalBack (iOS right-edge swipe). Returns true when navigation occurred. */
export function navigatePortalForward(navigate: NavigateFunction): boolean {
    const delta = getSafeForwardDelta();
    if (delta === 0) {
        return false;
    }
    pendingPopDirection = 'forward';
    navigate(delta);
    return true;
}

/**
 * Shared back handler for in-portal UI buttons, native gestures, and modals.
 * Returns true when navigation occurred.
 */
export function navigatePortalBack(
    navigate: NavigateFunction,
    fallbackRoute: string,
    options: PortalBackOptions = {},
): boolean {
    const { explicitTarget, useFallback = true } = options;

    if (explicitTarget) {
        navigate(explicitTarget);
        return true;
    }

    const delta = getSafeBackDelta();
    if (delta !== 0) {
        pendingPopDirection = 'back';
        navigate(delta);
        return true;
    }

    try {
        const backUrl = localStorage.getItem(BACK_URL_STORAGE_KEY);
        if (backUrl) {
            localStorage.removeItem(BACK_URL_STORAGE_KEY);
            navigate(backUrl);
            return true;
        }
    } catch {
        // ignore storage errors
    }

    if (useFallback) {
        navigate(fallbackRoute);
        return true;
    }

    return false;
}

/** @internal For tests */
export function getNavigationStackSnapshot(): readonly string[] {
    return navigationStack;
}

/** @internal For tests */
export function getForwardStackSnapshot(): readonly string[] {
    return forwardStack;
}
